<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  defineAsyncComponent,
  useTemplateRef,
  toRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useStaffChatAttachments } from 'dashboard/composables/useStaffChatAttachments';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { appendSignature } from 'dashboard/helper/editorHelper';
import { useMapGetter } from 'dashboard/composables/store';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import CopilotEditorSection from 'dashboard/components/widgets/conversation/CopilotEditorSection.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  chatId: {
    type: Number,
    required: true,
  },
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggleEditorSize']);

const STAFF_CHAT_CHANNEL_TYPE = 'Context::StaffChat';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

const { t } = useI18n();
const store = useStore();
const { isEditorHotKeyEnabled, fetchSignatureFlagFromUISettings } =
  useUISettings();
const messageSignature = useMapGetter('getMessageSignature');
const shortcutKey = useKbd(['$mod', '+', 'enter']);
const copilot = useCopilotReply();
const chatIdRef = toRef(props, 'chatId');
const {
  attachedFiles,
  hasAttachments,
  hasRecordedAudio,
  onFileUpload,
  onCannedAttachment,
  removeAttachment,
  clearAttachments,
  getFilesForUpload,
} = useStaffChatAttachments(chatIdRef);

const audioRecorderInput = useTemplateRef('audioRecorderInput');
const messageEditor = useTemplateRef('messageEditor');

const content = ref('');
const isSending = ref(false);
const isFocused = ref(false);
const showEmojiPicker = ref(false);
const showUserMentions = ref(false);
const showCannedMenu = ref(false);
const showVariablesMenu = ref(false);
const updateEditorSelectionWith = ref('');
const isRecordingAudio = ref(false);
const recordingAudioState = ref('');
const recordingAudioDurationText = ref('');

const hasContent = computed(() => !!content.value.trim());
const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(STAFF_CHAT_CHANNEL_TYPE)
);
const showAudioRecorderEditor = computed(() => isRecordingAudio.value);
const isDefaultEditorMode = computed(
  () => !showAudioRecorderEditor.value && !copilot.isActive.value
);
const editorId = computed(() => `staff-chat-editor-${props.chatId}`);
const messagePlaceholder = computed(() => t('CONVERSATION.FOOTER.MSG_INPUT'));
const sendButtonLabel = computed(() => {
  const keyLabel = isEditorHotKeyEnabled('cmd_enter')
    ? `(${shortcutKey.value})`
    : '(↵)';
  return `${t('CONVERSATION.REPLYBOX.SEND')} ${keyLabel}`;
});
const isSendDisabled = computed(() => {
  if (isSending.value) return true;
  if (hasAttachments.value || hasRecordedAudio.value) return false;
  return !hasContent.value;
});

const resetAudioRecorder = () => {
  isRecordingAudio.value = false;
  recordingAudioState.value = '';
  recordingAudioDurationText.value = '';
  attachedFiles.value = attachedFiles.value.filter(
    file => !file?.isRecordedAudio
  );
};

const toggleAudioRecorder = () => {
  isRecordingAudio.value = !isRecordingAudio.value;
  if (!isRecordingAudio.value) {
    resetAudioRecorder();
  }
};

const resetEditorContent = () => {
  let message = '';
  if (sendWithSignature.value && messageSignature.value) {
    message = appendSignature(
      message,
      messageSignature.value,
      STAFF_CHAT_CHANNEL_TYPE
    );
  }
  content.value = message;
};

watch(
  () => props.chatId,
  () => {
    resetEditorContent();
    clearAttachments();
    resetAudioRecorder();
    copilot.reset();
  }
);

onMounted(() => {
  resetEditorContent();
});

const sendMessage = async () => {
  if (isSendDisabled.value) return;

  const message = content.value.trim();
  const files = getFilesForUpload();

  isSending.value = true;
  try {
    await store.dispatch('staffChats/sendStaffMessage', {
      chatId: props.chatId,
      content: message,
      files,
    });
    resetEditorContent();
    clearAttachments();
    resetAudioRecorder();
    emitter.emit(BUS_EVENTS.MESSAGE_SENT);
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('CONVERSATION.MESSAGE_ERROR');
    useAlert(errorMessage);
  } finally {
    isSending.value = false;
  }
};

const onFocus = () => {
  isFocused.value = true;
};

const onBlur = () => {
  isFocused.value = false;
};

const toggleUserMention = value => {
  showUserMentions.value = value;
};

const toggleCannedMenu = value => {
  showCannedMenu.value = value;
};

const toggleVariablesMenu = value => {
  showVariablesMenu.value = value;
};

const clearEditorSelection = () => {
  showUserMentions.value = false;
  showCannedMenu.value = false;
  showVariablesMenu.value = false;
  updateEditorSelectionWith.value = '';
};

const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};

const hideEmojiPicker = () => {
  showEmojiPicker.value = false;
};

const addIntoEditor = emoji => {
  updateEditorSelectionWith.value = emoji;
};

const toggleEditorSize = () => {
  emit('toggleEditorSize');
  messageEditor.value?.focusEditorInputField?.();
};

const executeCopilotAction = (action, data) => {
  copilot.execute(action, data);
};

const onSubmitCopilotReply = () => {
  content.value = copilot.accept();
};

const toggleAudioRecorderPlayPause = () => {
  if (!audioRecorderInput.value) return;
  if (!recordingAudioState.value) {
    audioRecorderInput.value.stopRecording();
  } else {
    audioRecorderInput.value.playPause();
  }
};

const onRecordProgressChanged = duration => {
  recordingAudioDurationText.value = duration;
};

const onFinishRecorder = file => {
  recordingAudioState.value = 'stopped';
  if (!file) return;
  onFileUpload({ ...file, isRecordedAudio: true });
};

const onPaste = event => {
  if (!isDefaultEditorMode.value) return;

  Array.from(event.clipboardData.files)
    .filter(file => file.size > 0)
    .forEach(file => {
      onFileUpload({ file, name: file.name, type: file.type, size: file.size });
    });
};

const isAValidSendEvent = selectedKey =>
  !showUserMentions.value &&
  !showCannedMenu.value &&
  !showVariablesMenu.value &&
  isFocused.value &&
  isEditorHotKeyEnabled(selectedKey);

useKeyboardEvents({
  Escape: {
    action: () => hideEmojiPicker(),
    allowOnFocusedInput: true,
  },
  Enter: {
    action: e => {
      if (isAValidSendEvent('enter')) {
        sendMessage();
        e.preventDefault();
      }
    },
    allowOnFocusedInput: true,
  },
  '$mod+Enter': {
    action: e => {
      if (isAValidSendEvent('cmd_enter')) {
        sendMessage();
        e.preventDefault();
      }
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <div
    class="relative mx-2 mb-2 border rounded-xl border-n-weak bg-n-solid-1"
    :class="{
      'ring-1 ring-n-brand': isFocused || hasAttachments,
    }"
    @paste="onPaste"
  >
    <ReplyTopPanel
      :conversation-id="chatId"
      :has-content="hasContent || hasAttachments"
      :editor-content="content"
      :show-reply-mode-toggle="false"
      :disabled="
        (copilot.isActive.value && copilot.isButtonDisabled.value) ||
        showAudioRecorderEditor
      "
      @toggle-editor-size="toggleEditorSize"
      @execute-copilot-action="executeCopilotAction"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div
        :key="copilot.editorTransitionKey.value"
        class="relative px-3 py-0 -mt-px"
      >
        <EmojiInput
          v-if="showEmojiPicker"
          v-on-clickaway="hideEmojiPicker"
          :class="{
            'emoji-dialog--expanded': isOnExpandedLayout,
          }"
          :on-click="addIntoEditor"
        />
        <AudioRecorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="AUDIO_FORMATS.WAV"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
        />
        <CopilotEditorSection
          v-else-if="copilot.isActive.value"
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :placeholder="t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="copilot.showEditor.value = false"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
        />
        <WootMessageEditor
          v-else
          ref="messageEditor"
          v-model="content"
          :editor-id="editorId"
          :conversation-id="chatId"
          :update-selection-with="updateEditorSelectionWith"
          class="input popover-prosemirror-menu"
          :placeholder="messagePlaceholder"
          enable-variables
          enable-captain-tools
          channel-type="Context::StaffChat"
          :signature="messageSignature"
          allow-signature
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @canned-attachment="onCannedAttachment"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
          @execute-copilot-action="executeCopilotAction"
        />
        <div
          v-if="hasAttachments && isDefaultEditorMode"
          class="py-0 mb-2 bg-transparent"
        >
          <AttachmentPreview
            class="mt-2"
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
      </div>
    </Transition>
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <CopilotReplyBottomPanel
        v-if="copilot.isActive.value"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
      <ReplyBottomPanel
        v-else
        key="reply-bottom-panel"
        :conversation-id="chatId"
        :inbox="{}"
        portal-slug=""
        is-staff-chat
        :is-send-disabled="isSendDisabled"
        :is-recording-audio="isRecordingAudio"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :on-send="sendMessage"
        :send-button-text="sendButtonLabel"
        :show-emoji-picker="showEmojiPicker"
        show-file-upload
        show-audio-recorder
        enable-multiple-file-upload
        :on-file-upload="onFileUpload"
        :toggle-emoji-picker="toggleEmojiPicker"
        :toggle-audio-recorder="toggleAudioRecorder"
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      />
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}
</style>
